import { hasAnalyticsConsent, hasMarketingConsent } from '@/utils/cookieConsent';

type DataLayerEvent = {
  event?: string;
  [key: string]: unknown;
};

type GoogleTagCommand = unknown[];

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer: Array<DataLayerEvent | GoogleTagCommand>;
  }
}

export interface GA4Config {
  measurementId?: string;
}

export interface GA4Event {
  eventName: string;
  eventParams?: Record<string, unknown>;
}

class GA4Service {
  private initialized = false;

  initialize(_measurementId?: string): void {
    if (!this.ensureGoogleTagBridge()) {
      return;
    }

    this.initialized = true;
    this.updateConsent();
  }

  private ensureGoogleTagBridge(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    window.dataLayer = window.dataLayer || [];

    if (typeof window.gtag !== 'function') {
      window.gtag = function (...args: unknown[]) {
        window.dataLayer.push(args);
      };
    }

    this.initialized = true;
    return true;
  }

  updateConsent(): void {
    if (!this.ensureGoogleTagBridge() || !window.gtag) {
      return;
    }

    const analyticsConsent = hasAnalyticsConsent();
    const marketingConsent = hasMarketingConsent();

    window.gtag('consent', 'update', {
      analytics_storage: analyticsConsent ? 'granted' : 'denied',
      ad_storage: marketingConsent ? 'granted' : 'denied',
      ad_user_data: marketingConsent ? 'granted' : 'denied',
      ad_personalization: marketingConsent ? 'granted' : 'denied',
      personalization_storage: marketingConsent ? 'granted' : 'denied',
    });

    console.log('Google tag consent updated:', {
      analytics: analyticsConsent ? 'granted' : 'denied',
      marketing: marketingConsent ? 'granted' : 'denied',
    });
  }

  trackPageView(pageUrl: string, pageTitle?: string): void {
    if (!this.ensureGoogleTagBridge()) {
      return;
    }

    window.dataLayer.push({
      event: 'page_view',
      page_location: pageUrl,
      page_path: window.location.pathname + window.location.search,
      page_title: pageTitle || document.title,
    });
  }

  trackEvent(eventName: string, eventParams?: Record<string, unknown>): void {
    if (!this.ensureGoogleTagBridge()) {
      return;
    }

    window.dataLayer.push({
      event: this.mapEventName(eventName),
      ...(eventParams || {}),
    });
  }

  private mapEventName(eventName: string): string {
    const eventMap: Record<string, string> = {
      click: 'select_content',
      form_submission: 'generate_lead',
      content_generated: 'content_generation',
      feature_used: 'feature_usage',
      user_interaction: 'select_content',
    };

    return eventMap[eventName] || eventName;
  }

  trackFormSubmission(formName: string, success: boolean, additionalData?: Record<string, unknown>): void {
    this.trackEvent('form_submission', {
      form_name: formName,
      success,
      ...additionalData,
    });
  }

  trackContentGeneration(contentType: string, success: boolean, additionalData?: Record<string, unknown>): void {
    this.trackEvent('content_generated', {
      content_type: contentType,
      success,
      ...additionalData,
    });
  }

  trackFeatureUsage(featureName: string, metadata?: Record<string, unknown>): void {
    this.trackEvent('feature_used', {
      feature_name: featureName,
      ...metadata,
    });
  }

  trackClick(element: string, additionalData?: Record<string, unknown>): void {
    this.trackEvent('click', {
      element,
      ...additionalData,
    });
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  getMeasurementId(): string {
    return '';
  }
}

export const ga4Service = new GA4Service();
